import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { MemberStatistic } from '../../../services/api/statistics/statistics';
import { BenchmarkStatistics } from '@tassomutuo-backoffice/types';
import DashboardDropReasonPerMemberChart from './DashboardDropReasonPerMemberChart';
import DashboardLostRevenuePerMemberChart from './DashboardLostRevenuePerMemberChart';

export interface ChartDataPoint {
  name: string;
  value: number;
  color: string;
  fullName: string;
}

interface ProcessedReasonData {
  [reason: string]: ChartDataPoint[];
}

interface GroupedReasons {
  [phase: string]: ProcessedReasonData;
}

interface Props {
  teamMembersStatistics: MemberStatistic[];
  benchmarkStatistics?: BenchmarkStatistics;
  isPrinting: boolean;
}

// Colori per ogni consulente
const consultantColors: string[] = [
  '#4CAF50', // Verde
  '#FF9800', // Arancione
  '#9C27B0', // Viola
  '#2196F3', // Blu
  '#FFC107', // Giallo
  '#F44336', // Rosso
  '#00BCD4', // Ciano
  '#795548', // Marrone
  '#607D8B', // Blu grigio
  '#E91E63', // Rosa
  '#3F51B5', // Indaco
  '#FF5722', // Rosso arancione
  '#8BC34A', // Verde chiaro
  '#CDDC39', // Lime
  '#009688', // Teal
  '#673AB7', // Viola scuro
  '#FF6B6B', // Rosso coral
  '#4ECDC4', // Turchese
  '#45B7D1', // Azzurro
  '#96CEB4', // Verde menta
  '#FFEAA7', // Giallo pastello
  '#DDA0DD', // Prugna
  '#20B2AA', // Celeste scuro
  '#FF69B4', // Rosa shocking
  '#32CD32', // Verde lime
  '#FF1493', // Rosa profondo
];

// Raggruppa le motivazioni per fase
const phaseMapping: Record<string, string> = {
  'Non si è mai presentato all’appuntamento': 'Fase Pre Appuntamento',
  'Appuntamento Disdetto': 'Fase Pre Appuntamento',
  'Appuntamento andato male / Requisiti Bocciati': 'Fase Post Appuntamento',
  'Andato con un’altra offerta / Non vuole pagare la provvigione':
    'Fase Post Appuntamento',
  'Dilungamento / Altri Problemi': 'Fase Avanzata',
  'Pratica Bloccata dalla Banca': 'Fase Avanzata',
};

// Definisci l'ordine desiderato delle fasi
const phaseOrder = [
  'Fase Pre Appuntamento',
  'Fase Post Appuntamento',
  'Fase Avanzata',
];

const DashboardDeepDiveDropReasons: React.FC<Props> = ({
  teamMembersStatistics,
  benchmarkStatistics,
  isPrinting,
}) => {
  // Crea un mapping consistente per i colori dei consulenti
  const consultantColorMap = useMemo(() => {
    const sortedMembers = [...teamMembersStatistics].sort((a, b) =>
      a.user.name.localeCompare(b.user.name)
    );

    const colorMap: Record<string, string> = {};
    sortedMembers.forEach((member, index) => {
      colorMap[member.user.id] =
        consultantColors[index % consultantColors.length];
    });

    return colorMap;
  }, [teamMembersStatistics]);

  // Ordine consistente dei consulenti (alfabetico per nome)
  const sortedMembers = useMemo(() => {
    return [...teamMembersStatistics].sort((a, b) =>
      a.user.name.localeCompare(b.user.name)
    );
  }, [teamMembersStatistics]);

  // Funzione per ottenere i dati del benchmark per una specifica motivazione
  const getBenchmarkData = (reason: string): number => {
    if (!benchmarkStatistics) return 0;

    const benchmarkReason =
      benchmarkStatistics.benchmarkStatisticsAllPeriod.droppedReasons.percentages.find(
        (r) => r.label === reason
      );

    return benchmarkReason ? benchmarkReason.value : 0;
  };

  // Processa i dati per ottenere tutte le motivazioni uniche e calcola la differenza dal benchmark
  const processedData: ProcessedReasonData = useMemo(() => {
    // Raccogli tutte le motivazioni uniche
    const allReasons = new Set<string>();
    teamMembersStatistics.forEach((member) => {
      if (member.statistics?.droppedReasons?.percentages) {
        member.statistics.droppedReasons.percentages.forEach((reason) => {
          allReasons.add(reason.label);
        });
      }
    });

    // Crea i dati per ogni motivazione
    const reasonsData: ProcessedReasonData = {};
    Array.from(allReasons).forEach((reason) => {
      const benchmarkValue = getBenchmarkData(reason);

      reasonsData[reason] = sortedMembers.map((member) => {
        const memberName = member.user.name.split(' ').slice(1).join(' ');
        const reasonData = member.statistics?.droppedReasons?.percentages?.find(
          (r) => r.label === reason
        );

        const memberValue = reasonData ? reasonData.value : 0;
        const differenceFromBenchmark = memberValue - benchmarkValue;

        return {
          name: memberName,
          value: Number(differenceFromBenchmark.toFixed(2)), // Arrotonda a 2 decimali
          color: consultantColorMap[member.user.id],
          fullName: member.user.name,
        };
      });
    });

    return reasonsData;
  }, [sortedMembers, benchmarkStatistics, consultantColorMap]);

  // Processa i dati del fatturato perso
  const lostRevenueData: ChartDataPoint[] = useMemo(() => {
    return sortedMembers.map((member) => {
      const memberName = member.user.name.split(' ').slice(1).join(' ');
      const lostRevenue =
        member.statistics?.statisticsAllPeriod?.lostRevenue || 0;

      return {
        name: memberName,
        value: lostRevenue,
        color: consultantColorMap[member.user.id],
        fullName: member.user.name,
      };
    });
  }, [sortedMembers, consultantColorMap]);

  // Raggruppa le motivazioni per fase
  const groupedReasons: GroupedReasons = useMemo(() => {
    const grouped: GroupedReasons = {};

    Object.keys(processedData).forEach((reason) => {
      const phase = phaseMapping[reason];

      if (!phase) {
        return; // Salta se non c'è una fase associata
      }

      if (!grouped[phase]) {
        grouped[phase] = {};
      }

      grouped[phase][reason] = processedData[reason];
    });

    return grouped;
  }, [processedData]);

  return (
    <>
      <Box
        sx={{
          p: isPrinting ? 0 : 3,
          backgroundColor: isPrinting ? 'white' : '#FAFAFA',
          my: isPrinting ? 0 : 6,
          borderRadius: '8px',
          border: isPrinting ? 'none' : '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
          Motivazioni di Drop
        </Typography>
        <Typography
          variant="body2"
          color="primary"
          sx={{ mb: isPrinting ? 2 : 4, fontSize: '18px' }}
        >
          Tutte le motivazione di drop delle lead assegnate ai tuoi consulenti
          raggruppate e rappresentate nei grafici
        </Typography>

        {/* Legenda consulenti */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: isPrinting ? 0 : 3,
          }}
        >
          {sortedMembers.map((member) => (
            <Box
              key={member.user.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: consultantColorMap[member.user.id],
                  borderRadius: '30%',
                }}
              />
              <Typography variant="body2">{member.user.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Render delle sezioni raggruppate per fase nell'ordine desiderato */}
      {phaseOrder.map((phase, phaseIndex) => {
        const reasons = groupedReasons[phase];

        // Salta la fase se non ci sono dati per essa
        if (!reasons || Object.keys(reasons).length === 0) {
          return null;
        }

        return (
          <Box
            key={phase}
            data-page-break="after"
            sx={{
              px: 3,
              pt: isPrinting ? 0 : 3,
              pb: 3,
              backgroundColor: isPrinting ? 'white' : '#FAFAFA',
              my: isPrinting ? 0 : 6,
              borderRadius: '8px',
              border: isPrinting ? 'none' : '1px solid rgba(224, 224, 224, 1)',
            }}
          >
            <Typography
              color="primary"
              fontWeight="bold"
              variant="h5"
              sx={{ mb: 2, mt: 4, ml: 2 }}
            >
              {phase}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {Object.entries(reasons).map(([reasonTitle, reasonData]) => (
                <Box key={reasonTitle} minWidth={500} flex={1} width="100%">
                  <DashboardDropReasonPerMemberChart
                    title={reasonTitle}
                    data={reasonData}
                    benchmarkValue={getBenchmarkData(reasonTitle)}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}

      {/* Grafico del Fatturato Perso */}
      <Box
        sx={{
          p: 3,
          mt: 6,
          backgroundColor: isPrinting ? 'white' : '#FAFAFA',
          my: isPrinting ? 0 : 6,
          borderRadius: '8px',
          border: isPrinting ? 'none' : '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Typography
          color="primary"
          fontWeight="bold"
          variant="h5"
          sx={{ mb: 2, ml: 2 }}
        >
          Fatturato Perso
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box minWidth={500} flex={1} width="100%">
            <DashboardLostRevenuePerMemberChart data={lostRevenueData} />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DashboardDeepDiveDropReasons;
